import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../../components/Tooltip';
import { InfoIcon } from '../../../../../components/svgs/icons/InfoIcon';
import { styleSheet } from './styles';

// The date the per-contact engagement counters went live in production. Anything sent before it was
// never counted, so the rates below understate contacts who have been on the list longer — hence the
// disclaimer, which also has to name the inbox exclusion since only Levitate-sent mail is counted.
const ENGAGEMENT_TRACKING_START = 'June 27, 2026';

// Guards the warning only: a couple of unopened emails is not evidence of a neglected contact, and
// flagging one red would be a false accusation. A good open rate needs no such floor — there is no
// harm in crediting a contact who opened what little they were sent.
const MIN_EMAILS_FOR_WARNING = 5;
const HEALTHY_OPEN_RATE = 0.4;
const UNHEALTHY_OPEN_RATE = 0.1;

const formatRate = (rate: number | undefined, count: number, total: number) => {
	const value = rate ?? count / total;
	return `${Math.round(Math.min(1, value) * 100)}%`;
};

const getToneStyle = (openRate: number, total: number) => {
	if (openRate >= HEALTHY_OPEN_RATE) {
		return styleSheet.containerHealthy;
	}
	if (openRate < UNHEALTHY_OPEN_RATE && total > MIN_EMAILS_FOR_WARNING) {
		return styleSheet.containerUnhealthy;
	}
	return styleSheet.containerNeutral;
};

export const EngagementStats = ({ emailStats }: { emailStats?: Api.IEmailStats }) => {
	const total = emailStats?.totalEmails ?? 0;
	if (!emailStats || total === 0) {
		return null;
	}

	const metrics = [
		{ count: emailStats.totalOpens ?? 0, label: 'Opened', rate: emailStats.openRate },
		{ count: emailStats.totalClicks ?? 0, label: 'Clicked', rate: emailStats.clickRate },
		{ count: emailStats.totalReplies ?? 0, label: 'Replied', rate: emailStats.replyRate },
	];

	const openRate = emailStats.openRate ?? (emailStats.totalOpens ?? 0) / total;

	return (
		<div className={css(styleSheet.container, getToneStyle(openRate, total))}>
			<span className='indicator' />
			{metrics.map((metric, index) => (
				<span key={metric.label} className={css(styleSheet.metric)}>
					{index > 0 ? <span className={css(styleSheet.separator)}>&middot;</span> : null}
					<span>{metric.label}</span>
					<span className={css(styleSheet.value)}>{formatRate(metric.rate, metric.count, total)}</span>
				</span>
			))}
			<Tooltip>
				<TooltipTrigger asChild>
					<span className={css(styleSheet.infoIcon)} aria-label='About email engagement'>
						<InfoIcon />
					</span>
				</TooltipTrigger>
				<TooltipContent styles={[styleSheet.tooltip]}>
					<div className={css(styleSheet.tooltipCounts)}>
						{metrics.map(metric => `${metric.label} ${metric.count} of ${total}`).join(' · ')}
					</div>
					<div className={css(styleSheet.tooltipDisclaimer)}>
						Covers emails sent from Levitate since {ENGAGEMENT_TRACKING_START}. Emails sent earlier, or sent directly
						from your own inbox, are not counted.
					</div>
				</TooltipContent>
			</Tooltip>
		</div>
	);
};
